import React, { useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, Plus, Trash2, Save, X, ChevronLeft, ChevronRight, Upload } from 'lucide-react';
import { Autocomplete, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  createNcr,
  updateNcr,
  getNcrById,
  type Ncr,
  type NcrStage,
} from '../../services/ncrService.ts';
import { getAllNcrTemplates, type NcrTemplate } from '../../services/ncrTemplateService.ts';
import { getAllUsers, type User } from '../../services/userService.ts';
import './NcrEdit.css';

const NcrEdit: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const notify = useNotify();
  const isCreate = !id;
  const todayDate = new Date().toISOString().split('T')[0];
  const formatStatusLabel = (value?: string) => {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'in-progress') return 'In Progress';
    if (normalized === 'close') return 'Close';
    return 'Open';
  };

  // Form State
  const [formData, setFormData] = useState<Partial<Ncr>>({
    ncrNumber: '',
    description: '',
    raisedDate: todayDate,
    templateId: 0,
    ownerId: undefined,
    status: 'open',
    stages: [],
    properties: [],
  });

  const [templates, setTemplates] = useState<NcrTemplate[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(isCreate ? false : true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedStages, setExpandedStages] = useState<Set<number>>(new Set());
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowDraftLevels, setWorkflowDraftLevels] = useState<number[]>([]);

  const toDatePickerValue = (value?: string | null) => {
    if (!value) return null;
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const toDateOnlyString = (value: Date | null) => {
    if (!value) return undefined;
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load templates
        const [templateList, userList] = await Promise.all([
          getAllNcrTemplates(),
          getAllUsers(),
        ]);
        setTemplates(templateList);
        setUsers(userList);

        // Load NCR if editing
        if (!isCreate && id) {
          const ncrData = await getNcrById(Number(id));
          setFormData({
            ...ncrData,
            raisedDate: toDateOnlyString(toDatePickerValue(ncrData.raisedDate)),
            targetDate: toDateOnlyString(toDatePickerValue(ncrData.targetDate)),
          });
          // Auto-expand first stage for viewing
          if (ncrData.stages && ncrData.stages.length > 0) {
            setExpandedStages(new Set([ncrData.stages[0].id || 0]));
          }
        }
      } catch (error: any) {
        notify(error.message || 'Failed to load data', { type: 'warning' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isCreate, notify]);

  // Handle basic field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const selectedOwner = users.find((user) => user.id === formData.ownerId) || null;

  const selectedStageOwner = (ownerId?: number) => users.find((user) => user.id === ownerId) || null;

  // Handle template selection - load stages from template
  const handleTemplateChange = (templateId: number) => {
    const selectedTemplate = templates.find((t) => t.id === templateId);
    if (selectedTemplate) {
      const newStages: NcrStage[] = selectedTemplate.stages.map((tStage, index) => ({
        templateStageId: tStage.id,
        stageName: tStage.name,
        status: 'open',
        sortOrder: index,
        actions: [],
        properties: [],
      }));

      const newProperties = (selectedTemplate.properties || []).map((p) => ({
        templatePropertyId: p.id,
        propertyName: p.name,
        value: '',
        attachments: [],
      }));

      setFormData((prev) => ({
        ...prev,
        templateId,
        templateName: selectedTemplate.name,
        stages: newStages,
        properties: newProperties,
      }));

      // Clear expanded stages
      setExpandedStages(new Set());
      // Auto-expand first stage
      if (newStages.length > 0) {
        setExpandedStages(new Set([0]));
      }

      notify('Stages loaded from template', { type: 'info' });
    }
  };

  // Toggle stage expansion
  const toggleStageExpanded = (index: number) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedStages(newExpanded);
  };

  // Update stage field
  const updateStageField = (stageIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      stages[stageIndex] = {
        ...stages[stageIndex],
        [field]: value,
      };
      return { ...prev, stages };
    });
  };

  // Update NCR-level property
  const updateNcrProperty = (propIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      properties[propIndex] = {
        ...properties[propIndex],
        [field]: value,
      };
      return { ...prev, properties };
    });
  };

  // Add NCR-level property
  const addNcrProperty = () => {
    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      properties.push({
        templatePropertyId: undefined,
        propertyName: '',
        value: '',
        attachments: [],
      });
      return { ...prev, properties };
    });
  };

  // Remove NCR-level property
  const removeNcrProperty = (propIndex: number) => {
    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      properties.splice(propIndex, 1);
      return { ...prev, properties };
    });
  };

  // Upload attachments to NCR-level property (client-side staged files)
  const uploadNcrPropertyAttachments = (propIndex: number, files: FileList | null) => {
    if (!files || files.length === 0) return;

    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      const currentProperty = properties[propIndex] || { attachments: [] };
      const existingAttachments = [...(currentProperty.attachments || [])];

      const newAttachments = Array.from(files).map((file, index) => ({
        id: Date.now() + index,
        fileName: file.name,
        filePath: URL.createObjectURL(file),
        uploadedAt: new Date().toISOString(),
      }));

      properties[propIndex] = {
        ...currentProperty,
        attachments: [...existingAttachments, ...newAttachments],
      };
      return { ...prev, properties };
    });
  };

  const removeNcrPropertyAttachment = (propIndex: number, attachmentIndex: number) => {
    setFormData((prev) => {
      const properties = [...(prev.properties || [])];
      const attachments = [...(properties[propIndex]?.attachments || [])];
      const [removed] = attachments.splice(attachmentIndex, 1);

      if (removed?.filePath?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.filePath);
      }

      properties[propIndex] = {
        ...properties[propIndex],
        attachments,
      };
      return { ...prev, properties };
    });
  };

  // Add action to stage
  const addActionToStage = (stageIndex: number) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const currentStage = stages[stageIndex];
      stages[stageIndex] = {
        ...currentStage,
        actions: [
          ...(currentStage?.actions || []),
          {
            actionNumber: currentStage?.actions?.length ? currentStage.actions.length + 1 : 1,
            targetDate: undefined,
            closedDate: undefined,
            actionDetails: '',
            actionResponse: '',
            ownerId: undefined,
            status: 'open',
            attachments: [],
          },
        ],
      };
      return { ...prev, stages };
    });
  };

  // Update stage action field
  const updateStageAction = (stageIndex: number, actionIndex: number, field: string, value: any) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const actions = [...(stages[stageIndex]?.actions || [])];
      actions[actionIndex] = {
        ...actions[actionIndex],
        [field]: value,
      };
      stages[stageIndex] = {
        ...stages[stageIndex],
        actions,
      };
      return { ...prev, stages };
    });
  };

  // Remove action from stage
  const removeActionFromStage = (stageIndex: number, actionIndex: number) => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const actions = [...(stages[stageIndex]?.actions || [])];
      actions.splice(actionIndex, 1);
      stages[stageIndex] = {
        ...stages[stageIndex],
        actions,
      };
      return { ...prev, stages };
    });
  };

  // Workflow config modal handlers (Qpulse-style left/right indent)
  const openWorkflowConfig = () => {
    const stages = formData.stages || [];
    if (stages.length === 0) {
      notify('Please select template and load stages first', { type: 'warning' });
      return;
    }
    const initialLevels = stages.map((stage) => Math.max(0, Number(stage.workflowLevel ?? 0)));
    if (initialLevels.length > 0) {
      initialLevels[0] = 0;
    }
    setWorkflowDraftLevels(initialLevels);
    setWorkflowModalOpen(true);
  };

  const moveStageLeft = (index: number) => {
    if (index === 0) return;
    setWorkflowDraftLevels((prev) => {
      const next = [...prev];
      next[index] = Math.max(0, (next[index] ?? 0) - 1);
      return next;
    });
  };

  const moveStageRight = (index: number) => {
    if (index === 0) return;
    setWorkflowDraftLevels((prev) => {
      const next = [...prev];
      const maxAllowed = (next[index - 1] ?? 0) + 1;
      next[index] = Math.min(maxAllowed, (next[index] ?? 0) + 1);
      return next;
    });
  };

  const applyWorkflowConfig = () => {
    setFormData((prev) => {
      const stages = [...(prev.stages || [])];
      const normalizedLevels = workflowDraftLevels.map((level, index) => {
        if (index === 0) return 0;
        const maxAllowed = (workflowDraftLevels[index - 1] ?? 0) + 1;
        return Math.max(0, Math.min(level ?? 0, maxAllowed));
      });

      const nextStages = stages.map((stage, index) => {
        const level = normalizedLevels[index] ?? 0;
        let parentIndex = -1;
        if (level > 0) {
          for (let i = index - 1; i >= 0; i -= 1) {
            if ((normalizedLevels[i] ?? 0) === level - 1) {
              parentIndex = i;
              break;
            }
          }
        }

        const parentStage = parentIndex >= 0 ? stages[parentIndex] : undefined;

        return {
          ...stage,
          workflowLevel: level,
          parentStageId: parentStage?.id,
          parentTemplateStageId: parentStage?.templateStageId,
        };
      });

      return {
        ...prev,
        stages: nextStages,
      };
    });

    setWorkflowModalOpen(false);
    notify('Workflow configured successfully', { type: 'info' });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.ncrNumber?.trim()) {
      notify('NCR Number is required', { type: 'warning' });
      return;
    }

    if (!formData.templateId) {
      notify('Template selection is required', { type: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        raisedDate: toDateOnlyString(toDatePickerValue(formData.raisedDate || null)),
        targetDate: toDateOnlyString(toDatePickerValue(formData.targetDate || null)),
        stages: formData.stages?.map((stage) => ({
          templateStageId: stage.templateStageId,
          parentStageId: stage.parentStageId,
          parentTemplateStageId: stage.parentTemplateStageId,
          workflowLevel: stage.workflowLevel ?? 0,
          stageName: stage.stageName,
          ownerId: stage.ownerId,
          status: stage.status,
          properties: stage.properties,
          actions: stage.actions || [],
        })),
        properties: (formData.properties || []).map((prop) => ({
          templatePropertyId: prop.templatePropertyId,
          propertyName: prop.propertyName,
          value: prop.value,
          attachments: prop.attachments || [],
        })),
      };

      if (isCreate) {
        const result = await createNcr(submitData);
        notify('NCR created successfully', { type: 'info' });
        navigate(`/Ncr/${result.id}`);
      } else {
        await updateNcr(Number(id), submitData);
        notify('NCR updated successfully', { type: 'info' });
        navigate(`/Ncr/${id}`);
      }
    } catch (error: any) {
      notify(error.message || 'Failed to save NCR', { type: 'warning' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="ncr-edit-loading">Loading...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="ncr-edit-form">
      <div className="ncr-edit-header">
        <h1>{isCreate ? 'Create New NCR' : 'Edit NCR'}</h1>
        <div className="header-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/Ncr')}
          >
            <X size={18} /> Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
          >
            <Save size={18} /> {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <div className="ncr-edit-content">
        {/* NCR Basic Information */}
        <div className="form-section">
          <h2>NCR Information</h2>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="ncrNumber">NCR Number *</label>
              <input
                id="ncrNumber"
                type="text"
                value={formData.ncrNumber || ''}
                onChange={(e) => handleFieldChange('ncrNumber', e.target.value)}
                placeholder="e.g., NCR-2024-001"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="templateId">Select Template *</label>
              <select
                id="templateId"
                value={formData.templateId || 0}
                onChange={(e) => handleTemplateChange(Number(e.target.value))}
                required
              >
                <option value={0}>-- Select a Template --</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                placeholder="Enter NCR description"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="raisedDate">Raised Date</label>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={toDatePickerValue(formData.raisedDate)}
                  onChange={(newValue) => handleFieldChange('raisedDate', toDateOnlyString(newValue))}
                  slotProps={{
                    textField: {
                      id: 'raisedDate',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="form-group">
              <label htmlFor="targetDate">Target Date</label>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={toDatePickerValue(formData.targetDate)}
                  onChange={(newValue) => handleFieldChange('targetDate', toDateOnlyString(newValue))}
                  slotProps={{
                    textField: {
                      id: 'targetDate',
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </div>

            <div className="form-group">
              <label htmlFor="severity">Severity</label>
              <select
                id="severity"
                value={formData.severity || 'Low Risk'}
                onChange={(e) => handleFieldChange('severity', e.target.value)}
              >
                <option value="Low Risk">Low Risk</option>
                <option value="Medium Risk">Medium Risk</option>
                <option value="High Risk">High Risk</option>
              </select>
            </div>

            <div className="form-group full-width">
              <label>Owner</label>
              <Autocomplete
                options={users}
                value={selectedOwner}
                onChange={(_, value) => handleFieldChange('ownerId', value?.id)}
                getOptionLabel={(option) => option.userName || option.email || String(option.id)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                noOptionsText="No users found"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search and select owner"
                    size="small"
                  />
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    minHeight: 42,
                    borderRadius: '0.5rem',
                  },
                }}
              />
              {selectedOwner && (
                <p className="owner-hint-text">
                  {selectedOwner.roleName || 'User'}{selectedOwner.email ? ` • ${selectedOwner.email}` : ''}
                </p>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status || 'open'}
                onChange={(e) => handleFieldChange('status', e.target.value)}
              >
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="close">Close</option>
              </select>
            </div>
          </div>
        </div>

        {/* Stages Section */}
        {formData.stages && formData.stages.length > 0 && (
          <div className="form-section">
            <div className="section-header-with-action">
              <h2>Stages ({formData.stages.length})</h2>
              <button
                type="button"
                className="btn-sm btn-primary"
                onClick={openWorkflowConfig}
              >
                Config Workflow
              </button>
            </div>
            <div className="stages-list">
              {formData.stages.map((stage, stageIndex) => (
                <div key={stageIndex} className="stage-card">
                  <div
                    className="stage-header"
                    onClick={() => toggleStageExpanded(stageIndex)}
                  >
                    <div className="stage-title-section">
                      {expandedStages.has(stageIndex) ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                      <h3 style={{ marginLeft: `${(stage.workflowLevel ?? 0) * 20}px` }}>
                        Stage {stageIndex + 1}: {stage.stageName}
                        <span className="stage-level-badge">L{stage.workflowLevel ?? 0}</span>
                      </h3>
                    </div>
                    <span className={`stage-status stage-status-${stage.status}`}>
                      {formatStatusLabel(stage.status)}
                    </span>
                  </div>

                  {expandedStages.has(stageIndex) && (
                    <div className="stage-content">
                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label>Owner</label>
                          <Autocomplete
                            options={users}
                            value={selectedStageOwner(stage.ownerId)}
                            onChange={(_, value) => updateStageField(stageIndex, 'ownerId', value?.id)}
                            getOptionLabel={(option) => option.userName || option.email || String(option.id)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText="No users found"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Search and select stage owner"
                                size="small"
                              />
                            )}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                minHeight: 42,
                                borderRadius: '0.5rem',
                              },
                            }}
                          />
                        </div>

                        <div className="form-group">
                          <label>Status</label>
                          <select
                            value={stage.status || 'open'}
                            onChange={(e) => updateStageField(stageIndex, 'status', e.target.value)}
                          >
                            <option value="open">Open</option>
                            <option value="in-progress">In Progress</option>
                            <option value="close">Close</option>
                          </select>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="properties-section">
                        <div className="properties-header">
                          <h4>Actions</h4>
                          <button
                            type="button"
                            className="btn-sm btn-secondary"
                            onClick={() => addActionToStage(stageIndex)}
                          >
                            <Plus size={14} /> Add Action
                          </button>
                        </div>

                        {(stage.actions?.length || 0) === 0 ? (
                          <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
                            No actions yet. Click "Add Action" to create one.
                          </p>
                        ) : (
                          <table className="properties-table">
                            <thead>
                              <tr>
                                <th>Action No</th>
                                <th>Target Date</th>
                                <th>Owner</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stage.actions?.map((action, actionIndex) => (
                                <React.Fragment key={actionIndex}>
                                  <tr>
                                    <td>
                                      <input
                                        type="number"
                                        value={action.actionNumber ?? actionIndex + 1}
                                        onChange={(e) =>
                                          updateStageAction(
                                            stageIndex,
                                            actionIndex,
                                            'actionNumber',
                                            e.target.value ? Number(e.target.value) : undefined
                                          )
                                        }
                                        placeholder="Action no"
                                      />
                                    </td>
                                    <td>
                                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                          value={toDatePickerValue(action.targetDate)}
                                          onChange={(newValue) =>
                                            updateStageAction(
                                              stageIndex,
                                              actionIndex,
                                              'targetDate',
                                              toDateOnlyString(newValue)
                                            )
                                          }
                                          slotProps={{
                                            textField: {
                                              size: 'small',
                                              fullWidth: true,
                                            },
                                          }}
                                        />
                                      </LocalizationProvider>
                                    </td>
                                    <td>
                                      <Autocomplete
                                        options={users}
                                        value={selectedStageOwner(action.ownerId)}
                                        onChange={(_, value) =>
                                          updateStageAction(stageIndex, actionIndex, 'ownerId', value?.id)
                                        }
                                        getOptionLabel={(option) => option.userName || option.email || String(option.id)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        noOptionsText="No users found"
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            placeholder="Owner"
                                            size="small"
                                          />
                                        )}
                                        sx={{
                                          minWidth: 220,
                                          '& .MuiOutlinedInput-root': {
                                            minHeight: 42,
                                            borderRadius: '0.5rem',
                                          },
                                        }}
                                      />
                                    </td>
                                    <td>
                                      <select
                                        value={action.status || 'open'}
                                        onChange={(e) =>
                                          updateStageAction(
                                            stageIndex,
                                            actionIndex,
                                            'status',
                                            e.target.value
                                          )
                                        }
                                      >
                                        <option value="open">Open</option>
                                        <option value="in-progress">In Progress</option>
                                        <option value="close">Close</option>
                                      </select>
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn-icon btn-delete"
                                        onClick={() => removeActionFromStage(stageIndex, actionIndex)}
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </td>
                                  </tr>

                                  <tr>
                                    <td className="action-subrow-label">Action Details</td>
                                    <td colSpan={4}>
                                      <textarea
                                        value={action.actionDetails || ''}
                                        onChange={(e) =>
                                          updateStageAction(
                                            stageIndex,
                                            actionIndex,
                                            'actionDetails',
                                            e.target.value
                                          )
                                        }
                                        placeholder="Action details"
                                        rows={3}
                                      />
                                    </td>
                                  </tr>

                                  <tr>
                                    <td className="action-subrow-label">Action Response</td>
                                    <td colSpan={4}>
                                      <textarea
                                        value={action.actionResponse || ''}
                                        onChange={(e) =>
                                          updateStageAction(
                                            stageIndex,
                                            actionIndex,
                                            'actionResponse',
                                            e.target.value
                                          )
                                        }
                                        placeholder="Action response"
                                        rows={3}
                                      />
                                    </td>
                                  </tr>
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!formData.templateId && (
          <div className="empty-state">
            <p>Please select a template to view and configure stages</p>
          </div>
        )}

        {!!formData.templateId && (
          <div className="form-section">
            <h2>Properties</h2>
            <div className="properties-section" style={{ marginTop: 0 }}>
              <div className="properties-header">
                <h4>NCR Properties</h4>
                <button
                  type="button"
                  className="btn-sm btn-secondary"
                  onClick={addNcrProperty}
                >
                  <Plus size={14} /> Add Property
                </button>
              </div>

              {(formData.properties?.length || 0) === 0 ? (
                <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>
                  No properties yet. Click "Add Property" to create one.
                </p>
              ) : (
                <table className="properties-table">
                  <thead>
                    <tr>
                      <th>Property Name</th>
                      <th>Value</th>
                      <th>Attachments</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(formData.properties || []).map((prop, propIndex) => (
                      <tr key={propIndex}>
                        <td>
                          <input
                            type="text"
                            value={prop.propertyName || ''}
                            onChange={(e) => updateNcrProperty(propIndex, 'propertyName', e.target.value)}
                            placeholder="Property name"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={prop.value || ''}
                            onChange={(e) => updateNcrProperty(propIndex, 'value', e.target.value)}
                            placeholder="Property value"
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <label className="property-upload-btn" htmlFor={`ncr-prop-upload-${propIndex}`}>
                              <Upload size={14} /> Upload
                            </label>
                            <input
                              id={`ncr-prop-upload-${propIndex}`}
                              type="file"
                              multiple
                              style={{ display: 'none' }}
                              onChange={(e) => uploadNcrPropertyAttachments(propIndex, e.target.files)}
                            />

                            {(prop.attachments?.length || 0) > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                {prop.attachments?.map((att, attachmentIndex) => (
                                  <div key={`${att.fileName}-${attachmentIndex}`} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <a href={att.filePath} target="_blank" rel="noopener noreferrer" className="property-attachment-link">
                                      {att.fileName}
                                    </a>
                                    <button
                                      type="button"
                                      className="btn-icon btn-delete"
                                      onClick={() => removeNcrPropertyAttachment(propIndex, attachmentIndex)}
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => removeNcrProperty(propIndex)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {workflowModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content workflow-modal">
            <div className="modal-header">
              <h3>Config Workflow</h3>
              <button
                type="button"
                className="btn-close"
                onClick={() => setWorkflowModalOpen(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <p className="workflow-tip">
                Use left/right buttons to set hierarchy. Right = child level, Left = parent level.
              </p>
              <div className="workflow-list">
                {(formData.stages || []).map((stage, index) => {
                  const level = workflowDraftLevels[index] ?? 0;
                  const maxRight = index === 0 ? 0 : (workflowDraftLevels[index - 1] ?? 0) + 1;
                  return (
                    <div key={`${stage.templateStageId || stage.id || index}-${index}`} className="workflow-row">
                      <div className="workflow-row-actions">
                        <button
                          type="button"
                          className="btn-icon workflow-indent-btn"
                          onClick={() => moveStageLeft(index)}
                          disabled={index === 0 || level === 0}
                          title="Move Left"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <button
                          type="button"
                          className="btn-icon workflow-indent-btn"
                          onClick={() => moveStageRight(index)}
                          disabled={index === 0 || level >= maxRight}
                          title="Move Right"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>

                      <div className="workflow-stage-name" style={{ marginLeft: `${level * 24}px` }}>
                        <span className="workflow-stage-order">{index + 1}.</span>
                        <span>{stage.stageName || `Stage ${index + 1}`}</span>
                        <span className="workflow-level-tag">L{level}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setWorkflowModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={applyWorkflowConfig}
              >
                Apply Workflow
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default NcrEdit;
